/**
 * Helpers for free-form answer inputs:
 * - Unit hints: when the expected answer carries a unit ("12 cm", "50%"),
 *   the input shows that unit next to the field and appends it automatically,
 *   so the student only types the number.
 * - Variable prefixes: when the expected answer is "x = 8", the input shows
 *   "x =" in front of the field and prepends it automatically, so a child
 *   solving an equation types the number and still submits a full answer.
 * - Compound answers: "1 val. 30 min" or "3:45" get one field per part,
 *   so the student never has to remember the separator or the wording.
 * - Fraction preview: "1/3" typed by the student is rendered as a real
 *   trupmena so they can see they wrote what they meant.
 */

import { detectMultiAnswers, stripKatex } from "./answer-checking";

export interface UnitHint {
  /** Unit exactly as the exercise author wrote it: "cm", "€", "%", "°C". */
  unit: string;
  /** Spacing between number and unit as written in the expected answer. */
  separator: string;
}

export interface VarPrefix {
  /** Variable exactly as the exercise author wrote it: "x", "a", "a_1". */
  name: string;
  /** Spacing around "=" as written: "" for "x=8", " " for "x = 8". */
  separator: string;
}

/**
 * Shape of a free-form answer:
 * - `value`    — one value, optionally with a unit ("12 cm") and/or a
 *                variable in front of it ("x = 8")
 * - `compound` — several number+unit parts ("1 val. 30 min", "2 m 10 cm")
 * - `clock`    — a time of day ("3:45")
 */
export type AnswerFormat =
  | { kind: "value"; prefix?: VarPrefix; unit?: UnitHint }
  | { kind: "compound"; parts: UnitHint[] }
  | { kind: "clock" };

/**
 * Units recognised at the end of an expected answer. Written the way they
 * appear in exercises (both the typographic and ASCII variants).
 */
const UNIT_TOKENS = [
  "km/h", "m/s",
  "cm²", "cm2", "m²", "m2", "km²", "km2", "dm²", "dm2", "mm²", "mm2",
  "cm³", "cm3", "m³", "m3", "dm³", "dm3", "mm³", "mm3",
  "mm", "cm", "dm", "km", "m",
  "kg", "mg", "g", "t",
  "ml", "dl", "cl", "l",
  "min", "val", "sek", "s", "h",
  "°c", "°", "%", "€",
  "eurai", "euras", "eurų", "eur", "centai", "centas", "centų", "ct",
];

const UNITS_LONGEST_FIRST = [...UNIT_TOKENS].sort((a, b) => b.length - a.length);

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const UNIT_ALTERNATION = UNITS_LONGEST_FIRST.map(escapeRegex).join("|");
// Trailing "." allows abbreviations written as "20 val." — it stays part of
// the unit so the label matches how the exercise spells it.
const TRAILING_UNIT_RE = new RegExp(`^(.*?)(\\s*)(${UNIT_ALTERNATION})(\\.?)$`, "i");
const NUMBER = "-?\\d+(?:[.,]\\d+)?";
const COMPOUND_PART_RE = new RegExp(`(${NUMBER})(\\s*)(${UNIT_ALTERNATION})(\\.?)`, "gi");
const COMPOUND_RE = new RegExp(
  `^\\s*(?:${NUMBER}\\s*(?:${UNIT_ALTERNATION})\\.?)(?:\\s+${NUMBER}\\s*(?:${UNIT_ALTERNATION})\\.?){1,2}\\s*$`,
  "i"
);
// A single variable, optionally subscripted: "x", "y", "a_1", "a1".
const VAR_NAME = "[a-zA-Z](?:_?\\d)?";
const VAR_PREFIX_RE = new RegExp(`^(${VAR_NAME})(\\s*)=(\\s*)(\\S.*)$`);
const CLOCK_RE = /^(\d{1,2}):(\d{2})$/;
// "1:50" is a clock in a time problem and a scale in a drawing problem, so the
// question text decides — a ratio must never be split into hours and minutes.
const TIME_CONTEXT_RE = /valand|minu[čt]|laikrod|laikas|laiko|laiką|kada|kelinta|ryto|vakaro|popiet|val\./i;
const RATIO_CONTEXT_RE = /mastel|santyki|proporcij/i;

/** "12", "0,5", "3/4", "1 1/2" — the shapes a unit may be attached to. */
function isNumericAnswer(s: string): boolean {
  const v = s.trim();
  if (!v) return false;
  return (
    /^-?\d+([.,]\d+)?$/.test(v) ||
    /^-?\d+\s*\/\s*\d+$/.test(v) ||
    /^-?\d+\s+\d+\s*\/\s*\d+$/.test(v)
  );
}

/** Plain-text view of an expected answer (drops $...$ / \frac{}{} wrappers). */
function plainify(s: string): string {
  return stripKatex(s.replace(/\\text\{([^}]*)\}/g, "$1")).trim();
}

/** "12", "0,5", "3/4", "12 cm" — a value a variable may be equated to. */
function isValueAnswer(s: string): boolean {
  if (isNumericAnswer(s)) return true;
  const match = s.trim().match(TRAILING_UNIT_RE);
  return !!match && isNumericAnswer(match[1]);
}

/**
 * "x = 8" split into the variable shown beside the field and the value the
 * student types.
 *
 * Null unless the whole answer is one variable, "=" and a value. Answers that
 * are themselves an equation ("50 - x = 35") or a formula ("P = 4a") are the
 * thing being asked for, so they keep a plain field and no prefix.
 */
function splitVarPrefix(plain: string): { prefix: VarPrefix; value: string } | null {
  const match = plain.trim().match(VAR_PREFIX_RE);
  if (!match) return null;
  const [, name, before, after, value] = match;
  if (!isValueAnswer(value)) return null;
  return {
    prefix: { name, separator: before.length > 0 || after.length > 0 ? " " : "" },
    value: value.trim(),
  };
}

/** The value part of "x = 8", or the answer unchanged when there is no variable. */
function dropVarPrefix(plain: string): string {
  return splitVarPrefix(plain)?.value ?? plain;
}

/**
 * Variable carried by an expected answer, or null when there is none.
 *
 * The primary answer decides it. ";;" alternatives that drop the variable are
 * just tolerance for students who omit it, but an alternative naming a
 * *different* variable means no single prefix can be shown.
 */
export function detectVarPrefix(expected: string | null | undefined): VarPrefix | null {
  if (!expected?.trim()) return null;

  const [primary, ...alternatives] = expected.split(";;");
  const split = splitVarPrefix(plainify(primary));
  if (!split) return null;

  const conflicting = alternatives.some((alt) => {
    const altSplit = splitVarPrefix(plainify(alt));
    return altSplit && altSplit.prefix.name !== split.prefix.name;
  });

  return conflicting ? null : split.prefix;
}

function detectUnitSingle(expected: string): UnitHint | null {
  const plain = dropVarPrefix(plainify(expected));
  const match = plain.match(TRAILING_UNIT_RE);
  if (!match) return null;
  const [, numberPart, separator, unit, dot] = match;
  if (!isNumericAnswer(numberPart)) return null;
  return { unit: `${unit}${dot}`, separator: separator.length > 0 ? " " : "" };
}

/**
 * Unit carried by an expected answer, or null when there is none.
 *
 * The primary answer decides the unit. ";;" alternatives that carry no unit
 * are just tolerance for students who omit it, but an alternative in a
 * *different* unit (12 cm ;; 0,12 m) means no single unit can be shown.
 */
export function detectUnit(expected: string | null | undefined): UnitHint | null {
  if (!expected?.trim()) return null;

  const [primary, ...alternatives] = expected.split(";;");
  const hint = detectUnitSingle(primary);
  if (!hint) return null;

  const conflicting = alternatives.some((alt) => {
    const altHint = detectUnitSingle(alt);
    return altHint && altHint.unit.toLowerCase() !== hint.unit.toLowerCase();
  });

  return conflicting ? null : hint;
}

/** Parts of a "1 val. 30 min"-style answer, or null when it isn't one. */
function detectCompoundParts(expected: string): UnitHint[] | null {
  const plain = plainify(expected);
  if (!COMPOUND_RE.test(plain)) return null;

  const parts: UnitHint[] = [];
  for (const match of plain.matchAll(COMPOUND_PART_RE)) {
    const [, , separator, unit, dot] = match;
    parts.push({ unit: `${unit}${dot}`, separator: separator.length > 0 ? " " : "" });
  }
  if (parts.length < 2) return null;

  // "2 cm 3 cm" is not a compound answer — the parts must be different units.
  const distinct = new Set(parts.map((p) => p.unit.toLowerCase()));
  if (distinct.size !== parts.length) return null;

  return parts;
}

/** "3:45" is a time of day, but only in a question that is about time. */
function isClockAnswer(primary: string, context: string): boolean {
  const match = primary.match(CLOCK_RE);
  if (!match) return false;
  if (Number(match[1]) > 24 || Number(match[2]) > 59) return false;
  return TIME_CONTEXT_RE.test(context) && !RATIO_CONTEXT_RE.test(context);
}

/**
 * How an expected answer should be entered, or null for a plain single field.
 * The primary answer decides the shape; ";;" alternatives only add tolerance.
 * `context` is the question text — it tells a clock apart from a ratio.
 *
 * `allowPrefix` is false for fill-the-blank inputs, where a label in front of
 * the blank has nowhere to go and the blank sits inside the sentence anyway.
 */
export function detectAnswerFormat(
  expected: string | null | undefined,
  context = "",
  { allowPrefix = true }: { allowPrefix?: boolean } = {}
): AnswerFormat | null {
  if (!expected?.trim()) return null;

  const primary = plainify(expected.split(";;")[0]);
  if (isClockAnswer(primary, context)) return { kind: "clock" };

  const parts = detectCompoundParts(primary);
  if (parts) return { kind: "compound", parts };

  const prefix = allowPrefix ? detectVarPrefix(expected) : null;
  const unit = detectUnit(expected);
  if (!prefix && !unit) return null;

  return {
    kind: "value",
    ...(prefix ? { prefix } : {}),
    ...(unit ? { unit } : {}),
  };
}

/** The unit shown after a plain single field, if any. */
export function unitOf(format: AnswerFormat | null | undefined): UnitHint | null {
  return format?.kind === "value" ? format.unit ?? null : null;
}

/** The variable shown before a plain single field, if any. */
export function prefixOf(format: AnswerFormat | null | undefined): VarPrefix | null {
  return format?.kind === "value" ? format.prefix ?? null : null;
}

/** True when the student already typed a unit themselves. */
export function endsWithUnit(value: string): boolean {
  const match = value.trim().match(TRAILING_UNIT_RE);
  return !!match && isNumericAnswer(match[1]);
}

/**
 * Append the hinted unit to what the student typed.
 * Left untouched when they already wrote a unit, or when the answer is not
 * a bare number (a worded answer keeps its own wording).
 */
export function applyUnit(value: string, hint: UnitHint | null | undefined): string {
  const v = value.trim();
  if (!v || !hint) return v;
  if (endsWithUnit(v)) return v;
  if (!isNumericAnswer(v)) return v;
  return `${v}${hint.separator}${hint.unit}`;
}

/**
 * Prepend the hinted variable to what the student typed.
 * Left untouched when they wrote the variable themselves.
 */
export function applyVarPrefix(value: string, prefix: VarPrefix | null | undefined): string {
  const v = value.trim();
  if (!v || !prefix) return v;
  if (VAR_PREFIX_RE.test(v)) return v;
  return `${prefix.name}${prefix.separator}=${prefix.separator}${v}`;
}

/**
 * The answer as it should be graded and stored.
 * Compound and clock inputs already keep their assembled value in state;
 * a single field keeps the bare value and gets its variable and unit here.
 */
export function composeAnswer(value: string, format: AnswerFormat | null | undefined): string {
  if (format?.kind !== "value") return value.trim();
  return applyVarPrefix(applyUnit(value, format.unit), format.prefix);
}

/**
 * Inverse of composeAnswer — turns a stored answer back into what the input
 * should display (the variable and the unit live next to the field, not
 * inside it).
 */
export function toInputValue(value: string, format: AnswerFormat | null | undefined): string {
  if (format?.kind !== "value") return value;

  let stripped = value.trim();
  if (format.prefix) {
    const match = stripped.match(VAR_PREFIX_RE);
    if (match) stripped = match[4].trim();
  }
  if (format.unit) {
    const pattern = new RegExp(`\\s*${escapeRegex(format.unit.unit)}\\s*$`, "i");
    stripped = stripped.replace(pattern, "").trim();
  }
  return isNumericAnswer(stripped) ? stripped : value;
}

/** Split an assembled compound/clock answer back into its per-field values. */
export function splitParts(value: string, format: AnswerFormat): string[] {
  if (format.kind === "clock") {
    const [hours = "", minutes = ""] = value.split(":");
    return [hours.trim(), minutes.trim()];
  }
  if (format.kind !== "compound") return [value];

  // Walk left to right so a missing leading part (only "30 min" typed) does
  // not shift the remaining values into the wrong fields.
  let rest = value;
  return format.parts.map((part) => {
    const match = rest.match(new RegExp(`^\\s*(${NUMBER})\\s*${escapeRegex(part.unit)}`, "i"));
    if (!match) return "";
    rest = rest.slice(match[0].length);
    return match[1];
  });
}

/** Assemble per-field values into the answer that gets graded. */
export function joinParts(values: string[], format: AnswerFormat): string {
  if (format.kind === "clock") {
    const [hours, minutes] = values.map((v) => v.trim());
    if (!hours || !minutes) return "";
    return `${hours}:${minutes.length === 1 ? `0${minutes}` : minutes}`;
  }
  if (format.kind !== "compound") return values[0]?.trim() || "";

  return format.parts
    .map((part, i) => {
      const value = values[i]?.trim();
      return value ? `${value}${part.separator}${part.unit}` : "";
    })
    .filter(Boolean)
    .join(" ");
}

interface ProblemLike {
  id: string;
  expectedAnswer?: string | null;
  problemText?: string | null;
  answerType?: string | null;
  choices?: string[] | null;
}

/**
 * Answer formats for every input of a problem, keyed the same way answers are:
 * `problemId` for a single answer, `problemId__N` for sub-answers and blanks.
 */
export function buildAnswerFormats(problem: ProblemLike): Record<string, AnswerFormat> {
  const formats: Record<string, AnswerFormat> = {};
  if (!problem.expectedAnswer) return formats;
  // A multiple-choice answer is whatever the student clicked, word for word —
  // nothing to label and nothing to append.
  if (problem.choices?.length) return formats;

  const context = problem.problemText || "";

  if (problem.answerType === "fill_blanks") {
    let expected: string[];
    try {
      expected = JSON.parse(problem.expectedAnswer);
    } catch {
      return formats;
    }
    if (!Array.isArray(expected)) return formats;
    expected.forEach((answer, i) => {
      const format = detectAnswerFormat(String(answer), context, { allowPrefix: false });
      if (format) formats[`${problem.id}__${i}`] = format;
    });
    return formats;
  }

  const multi = detectMultiAnswers(problem.expectedAnswer, problem.problemText);
  if (multi) {
    multi.forEach((answer, i) => {
      const format = detectAnswerFormat(answer, context);
      if (format) formats[`${problem.id}__${i}`] = format;
    });
    return formats;
  }

  const format = detectAnswerFormat(problem.expectedAnswer, context);
  if (format) formats[problem.id] = format;
  return formats;
}

/** Merge the answer formats of many problems into one lookup table. */
export function buildAnswerFormatsFor(problems: ProblemLike[]): Record<string, AnswerFormat> {
  return problems.reduce<Record<string, AnswerFormat>>(
    (acc, problem) => Object.assign(acc, buildAnswerFormats(problem)),
    {}
  );
}

/**
 * LaTeX for a fraction the student typed, or null when the input is not
 * fraction-shaped. "3/4" -> \frac{3}{4}, "1 1/2" -> 1\frac{1}{2}.
 */
export function fractionLatex(value: string | null | undefined): string | null {
  const v = (value || "").trim();
  if (!v) return null;

  const mixed = v.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) {
    const [, whole, num, den] = mixed;
    if (Number(den) === 0) return null;
    return `${whole}\\frac{${num}}{${den}}`;
  }

  const simple = v.match(/^(-?)(\d+)\s*\/\s*(\d+)$/);
  if (simple) {
    const [, sign, num, den] = simple;
    if (Number(den) === 0) return null;
    return `${sign}\\frac{${num}}{${den}}`;
  }

  return null;
}
